import { Capacitor } from "@capacitor/core";
import {
  APP_DEEP_LINK_SCHEME,
  APP_TRUSTED_WEB_HOST_SUFFIXES,
  APP_WEB_ORIGIN,
} from "@nfp-tracker/utils";

const normalizeBaseUrl = (value?: string) => {
  if (!value) {
    return null;
  }

  try {
    const parsed = new URL(value);
    return parsed.origin;
  } catch {
    return null;
  }
};

const getWebAppOrigin = () => {
  const configuredOrigin = normalizeBaseUrl(import.meta.env.VITE_WEB_APP_URL as string | undefined);
  return configuredOrigin || APP_WEB_ORIGIN;
};

const getNativeAuthScheme = () => {
  const configured = (import.meta.env.VITE_NATIVE_AUTH_SCHEME as string | undefined)?.trim();
  const candidate = configured || APP_DEEP_LINK_SCHEME;
  return candidate.replace(/:\/\/$/, "").replace(/:$/, "");
};

const buildNativeDeepLinkUrl = (path: string) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${getNativeAuthScheme()}://${normalizedPath}`;
};

const isTrustedWebOrigin = (origin: string) => {
  try {
    const hostname = new URL(origin).hostname.toLowerCase();
    return APP_TRUSTED_WEB_HOST_SUFFIXES.some((suffix) =>
      hostname === suffix || hostname.endsWith(`.${suffix}`)
    );
  } catch {
    return false;
  }
};

export const buildAuthRedirectUrl = (path: string) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${buildAppReturnBaseUrl()}${normalizedPath}`;
};

export const buildAppReturnBaseUrl = () => {
  const fallbackOrigin = getWebAppOrigin();

  if (Capacitor.isNativePlatform()) {
    return `${getNativeAuthScheme()}://`;
  }

  return isTrustedWebOrigin(window.location.origin)
    ? window.location.origin
    : fallbackOrigin;
};

export const buildBillingReturnBaseUrl = () => {
  const fallbackOrigin = getWebAppOrigin();

  if (Capacitor.isNativePlatform()) {
    return fallbackOrigin;
  }

  return isTrustedWebOrigin(window.location.origin)
    ? window.location.origin
    : fallbackOrigin;
};

export const buildAppRedirectUrl = (path: string) => {
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return Capacitor.isNativePlatform()
    ? buildNativeDeepLinkUrl(normalizedPath)
    : `${buildAppReturnBaseUrl()}${normalizedPath}`;
};
